const API_BASE_URL = (process.env.VUE_APP_API_BASE_URL || 'http://localhost:8000/api')
  .replace('localhost', window.location.hostname)

let _accessToken = ''
let _refreshToken = ''
let _onAuthError = null
let _onTokenRefresh = null
let _refreshPromise = null

export function setOnAuthError(fn) {
  _onAuthError = fn
}

// Called whenever a silent 401-triggered refresh succeeds, so the caller can
// persist the (rotated, single-use) refresh token outside of this module's
// memory — otherwise a reload re-hydrates the stale, already-blacklisted
// token from storage and every subsequent refresh attempt 401s.
export function setOnTokenRefresh(fn) {
  _onTokenRefresh = fn
}

export function setTokens(access, refresh) {
  _accessToken = access || ''
  if (refresh !== undefined) _refreshToken = refresh || ''
}

export function getAccessToken() {
  return _accessToken
}

export function headers() {
  const h = { 'Content-Type': 'application/json' }
  if (_accessToken) h['Authorization'] = `Bearer ${_accessToken}`
  return h
}

async function refreshAccessToken() {
  if (!_refreshToken) return false
  if (!_refreshPromise) {
    _refreshPromise = fetch(`${API_BASE_URL}/v1/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh: _refreshToken }),
    })
      .then(async (response) => {
        if (!response.ok) return false
        const data = await response.json()
        const newRefresh = data.refresh ?? _refreshToken
        setTokens(data.access, newRefresh)
        if (_onTokenRefresh) _onTokenRefresh(data.access, newRefresh)
        return true
      })
      .catch(() => false)
      .finally(() => { _refreshPromise = null })
  }
  return _refreshPromise
}

export async function request(method, path, body, _retried = false) {
  let response
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method,
      headers: headers(),
      body: body ? JSON.stringify(body) : undefined,
    })
  } catch (e) {
    e.isNetworkError = true
    throw e
  }
  if (response.status === 401 && !_retried) {
    const refreshed = await refreshAccessToken()
    if (refreshed) return request(method, path, body, true)
  }
  if (!response.ok) {
    if ((response.status === 401 || response.status === 403) && _onAuthError) {
      _onAuthError()
    }
    // TODO: when _onAuthError fires above (redirect to /login already happening),
    // don't also throw the raw SimpleJWT `detail` text — callers dump it into a
    // toast, so the user sees e.g. "Given token not valid for any token type"
    // alongside the redirect instead of a clean re-login prompt. See TODO.md.
    const data = await response.json().catch(() => ({}))
    const err = new Error(data.detail || data.error || `HTTP ${response.status}`)
    err.status = response.status
    err.data = data
    throw err
  }
  if (response.status === 204) return null
  return response.json()
}

export { API_BASE_URL }
